import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Alert, Spin } from 'antd';

import { marked } from 'marked';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { sanitizeRichText } from 'services/SanitizeService';
import { Colors } from 'context';
import { VideoPlayer } from 'atoms';
import { CareerBookmarkButton, CareerLessonNotes } from 'molecules';
import { CareerLessonComplete, CareerLessonQuiz } from 'organisms';
import { authState, formatAttachmentSize, useCareerLesson, useMarkCareerLessonOpened } from 'core';
import { PATHS } from 'router/config';

import { useCareerAccessGuard } from './useCareerAccessGuard';

/**
 * /career/learning/:lessonId — a single career lesson (spec 0018 §6).
 *
 * The lock is entirely server-side: for a locked lesson the backend returns
 * `content: null`, `videoUrl: null`, `attachments: []` and this page renders
 * the teaser + a lock notice from `isUnlocked` alone. There is no client-side
 * unlock rule anywhere — completing the previous lesson is what flips the flag.
 */
export const CareerLesson = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const { active_role } = useReactiveVar(authState);

  useCareerAccessGuard();

  const { lesson, loading, error, refetch } = useCareerLesson(lessonId || '');
  const { markOpened } = useMarkCareerLessonOpened();

  // Fire the «просмотрен» ping exactly once per UNLOCKED lesson. The ref keyed on
  // lesson id survives `cache-and-network` refetches (which replace the `lesson`
  // object but keep its id) so the ping never double-fires, and re-fires only
  // when the student actually opens a different lesson (spec 0020 Фаза B).
  const openedPingRef = useRef<string | null>(null);

  useEffect(() => {
    if (!lesson || !lesson.isUnlocked) {
      return;
    }
    if (openedPingRef.current === lesson.id) {
      return;
    }
    openedPingRef.current = lesson.id;
    markOpened(lesson.id);
  }, [lesson, markOpened]);

  // Markdown authored in the ERP → HTML → allowlist-sanitized before it is
  // injected (spec 0020 — the same `marked` pipeline the regular lesson uses,
  // minus the turndown step, since career content is already Markdown source).
  const safeContent = useMemo(() => {
    if (!lesson?.content) {
      return '';
    }
    const rendered = marked.parse(lesson.content, { async: false }) as string;
    return sanitizeRichText(rendered);
  }, [lesson?.content]);

  const onCompleted = useCallback(() => {
    // The mutation already refetched the list + progress; this re-reads THIS
    // lesson so its own completed state (and the ✓) updates in place.
    refetch();
  }, [refetch]);

  if (error) {
    return <Alert type="error" showIcon message={t('careerCenter.lesson.loadError')} />;
  }

  if (loading && !lesson) {
    return (
      <SpinWrapper>
        <Spin size="large" />
      </SpinWrapper>
    );
  }

  if (!lesson) {
    return <Alert type="warning" showIcon message={t('careerCenter.lesson.notFound')} />;
  }

  return (
    <PageWrapper>
      <BackLink type="button" onClick={() => navigate(PATHS.careerLearning)}>
        ← {t('careerCenter.lesson.back')}
      </BackLink>

      <LessonHeader>
        <LessonHeaderMain>
          <LessonTitle>
            {lesson.order}. {lesson.title}
          </LessonTitle>
          {lesson.durationMin ? (
            <LessonMeta>
              {t('careerCenter.learning.duration', { minutes: lesson.durationMin })}
            </LessonMeta>
          ) : null}
        </LessonHeaderMain>
        {lesson.isUnlocked ? (
          <CareerBookmarkButton lessonId={lesson.id} isBookmarked={lesson.isBookmarked} />
        ) : null}
      </LessonHeader>

      {!lesson.isUnlocked ? (
        <>
          <LessonSummary>{lesson.summary}</LessonSummary>
          <LockedNotice>
            <LockIcon aria-hidden>🔒</LockIcon>
            <div>
              <LockedTitle>{t('careerCenter.lesson.lockedTitle')}</LockedTitle>
              <LockedText>{t('careerCenter.lesson.lockedText')}</LockedText>
            </div>
          </LockedNotice>
        </>
      ) : (
        <>
          {lesson.videoUrl ? (
            <VideoWrapper>
              <VideoPlayer src={lesson.videoUrl} active_role={active_role} />
            </VideoWrapper>
          ) : null}

          {safeContent ? (
            <LessonContent dangerouslySetInnerHTML={{ __html: safeContent }} />
          ) : (
            <LessonSummary>{lesson.summary}</LessonSummary>
          )}

          {lesson.attachments.length ? (
            <AttachmentsBlock>
              <AttachmentsTitle>{t('careerCenter.lesson.attachments')}</AttachmentsTitle>
              <AttachmentsList>
                {lesson.attachments.map((attachment) =>
                  attachment.url ? (
                    <AttachmentLink
                      key={attachment.id}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={attachment.originalName}>
                      <AttachmentIcon aria-hidden>📎</AttachmentIcon>
                      <AttachmentName>{attachment.originalName}</AttachmentName>
                      <AttachmentSize>{formatAttachmentSize(attachment.sizeBytes)}</AttachmentSize>
                    </AttachmentLink>
                  ) : null,
                )}
              </AttachmentsList>
            </AttachmentsBlock>
          ) : null}

          {lesson.quizQuestions.length ? (
            // Keyed per lesson so a graded attempt (local selections/score) can
            // never bleed into the next lesson when navigating between them.
            // The `quiz-` prefix is load-bearing: this element and the notes
            // below are siblings in the SAME children array, so a bare
            // `lesson.id` on both is a duplicate key — React's keyed-map pass
            // (taken as soon as any sibling slot is `null`, e.g. a lesson with
            // no video) then drops the first of the two fibers from its
            // deletion bookkeeping and leaves the old quiz DOM node behind on
            // every re-render, stacking identical copies.
            <CareerLessonQuiz
              key={`quiz-${lesson.id}`}
              lessonId={lesson.id}
              questions={lesson.quizQuestions}
            />
          ) : null}

          {/* Remounted per lesson so the textarea re-seeds from that lesson's
              `myNotes` instead of carrying edits across a navigation. The
              `notes-` prefix keeps this key distinct from the quiz's above. */}
          <CareerLessonNotes
            key={`notes-${lesson.id}`}
            lessonId={lesson.id}
            initialNotes={lesson.myNotes}
          />

          <CareerLessonComplete
            lessonId={lesson.id}
            completionKind={lesson.completionKind}
            artifactHint={lesson.artifactHint}
            isCompleted={lesson.isCompleted}
            completedAt={lesson.completedAt}
            onCompleted={onCompleted}
          />
        </>
      )}
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const BackLink = styled.button`
  margin-top: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.main};
  }
`;

const LessonHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin: 16px 0 24px 0;
`;

const LessonHeaderMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const LessonTitle = styled.h2`
  margin: 0 0 8px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const LessonMeta = styled.div`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const LessonSummary = styled.p`
  margin: 0 0 24px 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const VideoWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const LessonContent = styled.div`
  width: 100%;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}

  h1, h2, h3, h4 {
    margin: 24px 0 12px 0;
    ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
  }

  p {
    margin: 0 0 12px 0;
  }

  ul,
  ol {
    margin: 0 0 12px 0;
    padding-left: 24px;
  }

  a {
    color: ${Colors.main};
    text-decoration: underline;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  pre {
    padding: 16px;
    overflow-x: auto;
    border-radius: 8px;
    background-color: ${Colors.bg_gray};
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  td,
  th {
    padding: 8px 12px;
    border: 1px solid ${Colors.system_gray};
  }
`;

const LockedNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  padding: 24px;
  border: 1px dashed ${Colors.system_gray};
  border-radius: 12px;
  background-color: ${Colors.bg_gray};
`;

const LockIcon = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const LockedTitle = styled.div`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const LockedText = styled.div`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const AttachmentsBlock = styled.div`
  width: 100%;
  margin-top: 32px;
`;

const AttachmentsTitle = styled.h3`
  margin: 0 0 12px 0;
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.bold })}
`;

const AttachmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AttachmentLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;
  color: ${Colors.black};
  text-decoration: none;

  &:hover {
    border-color: ${Colors.main};
  }
`;

const AttachmentIcon = styled.span`
  flex-shrink: 0;
`;

const AttachmentName = styled.span`
  flex-grow: 1;
  word-break: break-all;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const AttachmentSize = styled.span`
  flex-shrink: 0;
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;
